import { useState } from 'react'
import { Button } from '../Common/Button'
import { StatusChip } from '../Common'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import UndoRoundedIcon from '@mui/icons-material/UndoRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { updateWhatsappCommandOutcome } from '../../services/whatsappService'

export type WhatsappCommandData =
  | {
      type: 'JOB_DONE'
      itemId: string
      itemTitle: string
      proofSubmissionId?: string
      photoUrl?: string
      outcome?: 'ACCEPTED' | 'SENT_BACK' | null
    }
  | {
      type: 'EXPENSE_DRAFT'
      expenseId?: string
      receiptUrl?: string
      outcome?: 'OPENED' | null
    }
  | {
      type: 'COMMENT'
      itemId: string
      itemTitle: string
      commentId?: string
      commentText: string
      outcome?: 'ACCEPTED' | 'SENT_BACK' | 'OPENED' | null
    }
  | {
      type: 'LEAVE_REQUEST'
      requestId: string
      learnerId: string
      learnerName?: string
      fromDate: string
      toDate: string
      reason: string
      outcome?: 'OPENED' | null
    }
  | {
      type: 'HOMEWORK_SUBMISSION'
      submissionId?: string
      checklistId: string
      learnerId: string
      learnerName?: string
      checklistTitle?: string
      photoUrl?: string
      outcome?: 'OPENED' | null
    }

export interface WhatsappCommandCardProps {
  command: WhatsappCommandData
  workspaceId?: string
  channelId?: string
  messageId?: string
  onAcceptJobDone?: (itemId: string) => Promise<void> | void
  onSendBackJobDone?: (itemId: string) => Promise<void> | void
  onOpenExpense?: (expenseId?: string) => void
  onOpenJob?: (itemId: string) => void
  onOpenLeaveRequest?: (requestId: string) => void
  onOpenHomeworkSubmission?: (checklistId: string, learnerId: string) => void
}

export const WhatsappCommandCard = ({
  command,
  workspaceId,
  channelId,
  messageId,
  onAcceptJobDone,
  onSendBackJobDone,
  onOpenExpense,
  onOpenJob,
  onOpenLeaveRequest,
  onOpenHomeworkSubmission,
}: WhatsappCommandCardProps) => {
  const [outcome, setOutcome] = useState<'ACCEPTED' | 'SENT_BACK' | 'OPENED' | null>(() => {
    return command.outcome === 'ACCEPTED' || command.outcome === 'SENT_BACK' || command.outcome === 'OPENED'
      ? command.outcome
      : null
  })
  const [busy, setBusy] = useState(false)

  const persistOutcome = async (nextOutcome: 'ACCEPTED' | 'SENT_BACK' | 'OPENED') => {
    setOutcome(nextOutcome)
    if (workspaceId && channelId && messageId) {
      try {
        await updateWhatsappCommandOutcome({
          workspaceId,
          channelId,
          messageId,
          outcome: nextOutcome,
        })
      } catch (err) {
        console.warn('[WhatsappCommandCard] Failed to persist outcome:', err)
      }
    }
  }

  if (command.type === 'JOB_DONE') {
    return (
      <div
        className="mt-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-3 text-sm"
        data-testid="whatsapp-command-card-job-done"
        role="region"
        aria-label={`Job review for ${command.itemTitle}`}
      >
        <div className="flex items-center justify-between gap-2">
          <strong className="font-medium text-[var(--text-strong)]">{command.itemTitle}</strong>
          {outcome ? (
            <StatusChip
              tone={outcome === 'ACCEPTED' ? 'success' : 'danger'}
              size="sm"
              data-testid="whatsapp-card-outcome"
            >
              {outcome === 'ACCEPTED' ? 'Accepted' : 'Sent back'}
            </StatusChip>
          ) : (
            <StatusChip tone="warning" size="sm">Review needed</StatusChip>
          )}
        </div>

        {command.photoUrl && (
          <div className="mt-2 overflow-hidden rounded border border-[var(--border-subtle)]">
            <img
              src={command.photoUrl}
              alt={`Proof for ${command.itemTitle}`}
              className="max-h-48 w-full object-cover"
              loading="lazy"
            />
          </div>
        )}

        {!outcome && (
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              tone="danger"
              size="sm"
              disabled={busy}
              icon={<UndoRoundedIcon />}
              data-testid="whatsapp-card-send-back-btn"
              onClick={async () => {
                setBusy(true)
                try {
                  if (onSendBackJobDone) {
                    await onSendBackJobDone(command.itemId)
                  }
                  await persistOutcome('SENT_BACK')
                } finally {
                  setBusy(false)
                }
              }}
            >
              Send back
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              icon={<CheckRoundedIcon />}
              data-testid="whatsapp-card-accept-btn"
              onClick={async () => {
                setBusy(true)
                try {
                  if (onAcceptJobDone) {
                    await onAcceptJobDone(command.itemId)
                  }
                  await persistOutcome('ACCEPTED')
                } finally {
                  setBusy(false)
                }
              }}
            >
              Accept
            </Button>
          </div>
        )}
      </div>
    )
  }

  if (command.type === 'EXPENSE_DRAFT') {
    return (
      <div
        className="mt-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-3 text-sm"
        data-testid="whatsapp-command-card-expense-draft"
        role="region"
        aria-label="Expense draft submission"
      >
        <div className="flex items-center justify-between gap-2">
          <strong className="font-medium text-[var(--text-strong)]">Expense intake draft</strong>
          {outcome === 'OPENED' ? (
            <StatusChip tone="neutral" size="sm" data-testid="whatsapp-card-outcome">Opened</StatusChip>
          ) : (
            <StatusChip tone="warning" size="sm">Draft</StatusChip>
          )}
        </div>

        {command.receiptUrl && (
          <div className="mt-2 overflow-hidden rounded border border-[var(--border-subtle)]">
            <img
              src={command.receiptUrl}
              alt="Expense receipt"
              className="max-h-48 w-full object-cover"
              loading="lazy"
            />
          </div>
        )}

        <div className="mt-3 flex items-center justify-end">
          <Button
            variant="secondary"
            size="sm"
            icon={<OpenInNewRoundedIcon />}
            data-testid="whatsapp-card-open-expense-btn"
            onClick={() => {
              void persistOutcome('OPENED')
              if (onOpenExpense) {
                onOpenExpense(command.expenseId)
              }
            }}
          >
            Open
          </Button>
        </div>
      </div>
    )
  }

  if (command.type === 'COMMENT') {
    return (
      <div
        className="mt-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-3 text-sm"
        data-testid="whatsapp-command-card-comment"
        role="region"
        aria-label={`Comment on ${command.itemTitle}`}
      >
        <div className="flex items-center justify-between gap-2">
          <strong className="font-medium text-[var(--text-strong)]">{command.itemTitle}</strong>
          <StatusChip tone="neutral" size="sm">Comment</StatusChip>
        </div>
        <p className="mt-1 text-xs text-[var(--text-muted)] italic">
          "{command.commentText}"
        </p>
        <div className="mt-2 flex items-center justify-end">
          <Button
            variant="link"
            size="sm"
            icon={<LaunchRoundedIcon />}
            data-testid="whatsapp-card-link-job-btn"
            onClick={() => {
              if (onOpenJob) {
                onOpenJob(command.itemId)
              }
            }}
          >
            View job
          </Button>
        </div>
      </div>
    )
  }

  if (command.type === 'LEAVE_REQUEST') {
    const dateDisplay =
      command.fromDate === command.toDate
        ? command.fromDate
        : `${command.fromDate} to ${command.toDate}`

    return (
      <div
        className="mt-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-3 text-sm"
        data-testid="whatsapp-command-card-leave-request"
        role="region"
        aria-label={`Leave request for ${command.learnerName || 'Learner'}`}
      >
        <div className="flex items-center justify-between gap-2">
          <strong className="font-medium text-[var(--text-strong)]">
            Leave request: {command.learnerName || 'Learner'}
          </strong>
          {outcome === 'OPENED' ? (
            <StatusChip tone="neutral" size="sm" data-testid="whatsapp-card-outcome">
              Opened
            </StatusChip>
          ) : (
            <StatusChip tone="warning" size="sm">
              Pending
            </StatusChip>
          )}
        </div>
        <p className="mt-1 text-xs text-[var(--text-muted)] font-medium">
          Dates: {dateDisplay}
        </p>
        <p className="mt-1 text-xs text-[var(--text-muted)] italic">
          "{command.reason}"
        </p>
        <div className="mt-3 flex items-center justify-end">
          <Button
            variant="secondary"
            size="sm"
            icon={<OpenInNewRoundedIcon />}
            data-testid="whatsapp-card-open-leave-btn"
            onClick={() => {
              void persistOutcome('OPENED')
              if (onOpenLeaveRequest) {
                onOpenLeaveRequest(command.requestId)
              }
            }}
          >
            Open
          </Button>
        </div>
      </div>
    )
  }

  if (command.type === 'HOMEWORK_SUBMISSION') {
    return (
      <div
        className="mt-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-3 text-sm"
        data-testid="whatsapp-command-card-homework-submission"
        role="region"
        aria-label={`Homework submission for ${command.learnerName || 'Learner'}`}
      >
        <div className="flex items-center justify-between gap-2">
          <strong className="font-medium text-[var(--text-strong)]">
            {command.checklistTitle || 'Homework'}: {command.learnerName || 'Learner'}
          </strong>
          {outcome === 'OPENED' ? (
            <StatusChip tone="neutral" size="sm" data-testid="whatsapp-card-outcome">
              Opened
            </StatusChip>
          ) : (
            <StatusChip tone="info" size="sm">
              Submitted
            </StatusChip>
          )}
        </div>

        {command.photoUrl && (
          <div className="mt-2 overflow-hidden rounded border border-[var(--border-subtle)]">
            <img
              src={command.photoUrl}
              alt="Homework submission"
              className="max-h-48 w-full object-cover"
              loading="lazy"
            />
          </div>
        )}

        <div className="mt-3 flex items-center justify-end">
          <Button
            variant="secondary"
            size="sm"
            icon={<OpenInNewRoundedIcon />}
            data-testid="whatsapp-card-open-hw-btn"
            onClick={() => {
              void persistOutcome('OPENED')
              if (onOpenHomeworkSubmission) {
                onOpenHomeworkSubmission(command.checklistId, command.learnerId)
              }
            }}
          >
            Open
          </Button>
        </div>
      </div>
    )
  }

  return null
}
